'use client';

import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import Clarity from '@microsoft/clarity';
import { isInternalUserEmail } from '@/components/analytics/analytics-user.helper';
import { analyticsLog } from '@/components/analytics/analytics-logger.helper';

declare global {
  interface Window {
    dataLayer: unknown[];
    __metercubeAnalyticsBootstrapped?: boolean;
  }
}

interface AnalyticsConsentManagerProps {
  gaId: string;
  clarityId: string;
}

const buildGaConfig = (
  userId?: string,
  extra: Record<string, unknown> = {}
) => ({
  anonymize_ip: true,
  debug_mode: process.env.NODE_ENV !== 'production',
  send_page_view: false,
  ...(userId ? { user_id: userId } : {}),
  ...extra,
});

const buildConsentState = (isGranted: boolean) => ({
  ad_storage: 'denied' as const,
  ad_user_data: 'denied' as const,
  ad_personalization: 'denied' as const,
  analytics_storage: isGranted ? ('granted' as const) : ('denied' as const),
});

const isTrue = (value: unknown) => String(value).toLowerCase() === 'true';

type AnalyticsWindow = Window & {
  gtag?: (...args: any[]) => void;
};

const sendGaConfig = (
  analyticsWindow: AnalyticsWindow,
  gaId: string,
  userId?: string,
  extra: Record<string, unknown> = {}
) => {
  analyticsLog('info', 'gtag config', { gaId, userId, extra });
  analyticsWindow.gtag?.('config', gaId, buildGaConfig(userId, extra));
};

const AnalyticsConsentManager = ({
  gaId,
  clarityId,
}: AnalyticsConsentManagerProps) => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const hasInitializedClarity = useRef(false);

  const hasAcceptedTerms = isTrue(session?.user?.consent);
  const hasTrackingConsent = isTrue(session?.user?.tracking);
  const isAnalyticsGranted = hasAcceptedTerms && hasTrackingConsent;

  const sessionEmail = session?.user?.email;
  const isAuthenticated = status === 'authenticated';
  const isSessionEmailReady =
    !isAuthenticated || typeof sessionEmail === 'string';

  const isInternalUser = isInternalUserEmail(sessionEmail);
  const userId =
    isAnalyticsGranted && session?.user?.id
      ? session.user.id.toString()
      : undefined;

  const canRunAnalytics =
    !!gaId && status !== 'loading' && isSessionEmailReady && !isInternalUser;

  // ─── 1. Bootstrap ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!gaId || typeof window === 'undefined') return;
    if (window.__metercubeAnalyticsBootstrapped) return;

    const analyticsWindow = window as AnalyticsWindow;

    analyticsLog('info', 'BOOTSTRAP effect fired', {
      gaId,
      windowHasGtag: typeof analyticsWindow.gtag === 'function',
      gaInlineRan: !!(window as any).__gaInlineRan,
      dataLayerLength: window.dataLayer?.length ?? 'undefined',
      alreadyBootstrapped: !!window.__metercubeAnalyticsBootstrapped,
    });

    // Define gtag if the _document.tsx inline script was CSP-blocked
    window.dataLayer = window.dataLayer || [];
    if (typeof analyticsWindow.gtag !== 'function') {
      analyticsLog(
        'warn',
        'BOOTSTRAP — gtag missing, defining fallback + re-running consent default'
      );
      analyticsWindow.gtag = function (...args: unknown[]) {
        window.dataLayer.push(args);
      };
      // Re-run the setup that the blocked inline script should have done
      analyticsWindow.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
      analyticsWindow.gtag('js', new Date());
      sendGaConfig(analyticsWindow, gaId);
    } else {
      analyticsLog(
        'success',
        'BOOTSTRAP — gtag already defined by _document.tsx inline script'
      );
    }

    const scriptExists = !!document.querySelector(
      `script[src="https://www.googletagmanager.com/gtag/js?id=${gaId}"]`
    );
    analyticsLog('info', 'BOOTSTRAP — gtag.js script tag check', {
      scriptExists,
    });

    if (!scriptExists) {
      analyticsLog('warn', 'BOOTSTRAP — injecting gtag.js script');
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);
    }

    window.__metercubeAnalyticsBootstrapped = true;
    analyticsLog('success', 'BOOTSTRAP complete');
  }, [gaId]);

  // ─── 2. Consent update ─────────────────────────────────────────────────────
  useEffect(() => {
    const analyticsWindow = window as AnalyticsWindow;

    analyticsLog('info', 'CONSENT EFFECT fired', {
      canRunAnalytics,
      isAnalyticsGranted,
      hasAcceptedTerms,
      hasTrackingConsent,
      status,
      userId,
      gtagAvailable: typeof analyticsWindow.gtag === 'function',
    });

    if (!canRunAnalytics || !analyticsWindow.gtag) {
      analyticsLog('warn', 'CONSENT EFFECT skipped', {
        reason: !canRunAnalytics
          ? 'canRunAnalytics is false'
          : 'gtag not available',
      });
      return;
    }

    const consentState = buildConsentState(isAnalyticsGranted);
    analyticsLog(
      isAnalyticsGranted ? 'success' : 'warn',
      `CONSENT UPDATE → analytics_storage: ${consentState.analytics_storage}`,
      consentState
    );
    analyticsWindow.gtag('consent', 'update', consentState);

    sendGaConfig(analyticsWindow, gaId, userId);
  }, [
    canRunAnalytics,
    gaId,
    hasAcceptedTerms,
    hasTrackingConsent,
    isAnalyticsGranted,
    userId,
  ]);

  // ─── 3. Clarity ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!clarityId) return;
    if (status === 'loading' || !isSessionEmailReady) return;
    if (isInternalUser) return;

    analyticsLog('info', 'CLARITY effect fired', {
      clarityId,
      isAnalyticsGranted,
      userId,
      alreadyInitialized: hasInitializedClarity.current,
    });

    if (!hasInitializedClarity.current) {
      Clarity.init(clarityId);
      hasInitializedClarity.current = true;
      analyticsLog('success', 'CLARITY initialized');
    }

    Clarity.consentV2({
      ad_Storage: 'denied',
      analytics_Storage: isAnalyticsGranted ? 'granted' : 'denied',
    });

    if (isAnalyticsGranted && session?.user?.id) {
      Clarity.identify(session.user.id.toString());
      analyticsLog('success', 'CLARITY identify called', { userId });
    }
  }, [
    clarityId,
    isAnalyticsGranted,
    isInternalUser,
    isSessionEmailReady,
    session?.user?.id,
    status,
  ]);

  // ─── 4a. sendPageView helper ───────────────────────────────────────────────
  const sendPageView = (url: string) => {
    const analyticsWindow = window as AnalyticsWindow;
    const pageLocation = new URL(url, window.location.origin).toString();

    analyticsLog('info', 'sendPageView called', {
      url,
      pageLocation,
      pageTitle: document.title,
      userId,
      gtagAvailable: typeof analyticsWindow.gtag === 'function',
      dataLayerLength: window.dataLayer?.length,
    });

    sendGaConfig(analyticsWindow, gaId, userId, {
      page_path: url,
      page_location: pageLocation,
      page_title: document.title,
    });

    analyticsWindow.gtag?.('event', 'page_view', {
      page_path: url,
      page_location: pageLocation,
      page_title: document.title,
      ...(userId ? { user_id: userId } : {}),
      debug_mode: process.env.NODE_ENV !== 'production',
    });

    analyticsLog('success', 'page_view event fired', { url });
  };

  // ─── 4b. Initial page view ─────────────────────────────────────────────────
  useEffect(() => {
    const analyticsWindow = window as AnalyticsWindow;

    analyticsLog('info', 'INITIAL PAGE VIEW effect fired', {
      canRunAnalytics,
      isAnalyticsGranted,
      path: router.asPath,
      gtagAvailable: typeof analyticsWindow.gtag === 'function',
    });

    if (!canRunAnalytics || !analyticsWindow.gtag || !isAnalyticsGranted) {
      analyticsLog('warn', 'INITIAL PAGE VIEW skipped', {
        canRunAnalytics,
        isAnalyticsGranted,
        gtagAvailable: typeof analyticsWindow.gtag === 'function',
      });
      return;
    }

    sendPageView(router.asPath);
  }, [canRunAnalytics, isAnalyticsGranted]);

  // ─── 4c. Route change listener ────────────────────────────────────────────
  useEffect(() => {
    const analyticsWindow = window as AnalyticsWindow;

    analyticsLog('info', 'ROUTE LISTENER effect fired', {
      canRunAnalytics,
      isAnalyticsGranted,
      gtagAvailable: typeof analyticsWindow.gtag === 'function',
    });

    if (!canRunAnalytics || !analyticsWindow.gtag || !isAnalyticsGranted) {
      analyticsLog('warn', 'ROUTE LISTENER skipped', {
        canRunAnalytics,
        isAnalyticsGranted,
      });
      return;
    }

    const handleRouteChange = (url: string) => {
      analyticsLog('info', 'routeChangeComplete fired', { url });
      sendPageView(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    analyticsLog('success', 'ROUTE LISTENER registered');

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      analyticsLog('warn', 'ROUTE LISTENER unregistered');
    };
  }, [canRunAnalytics, isAnalyticsGranted, router.events]);

  return <></>;
};

export default AnalyticsConsentManager;
